import { Box, FormControl, MenuItem, Select } from '@mui/material';
import { ReactComponent as Vector } from '../../../assets/PepWise2.0/arrow-down-dark.svg';
import { useTheme } from '@mui/material/styles';

type DropdownOption = string | {
  label: string;
  value: string;
};

type SimpleDropdownFilterProps = {
  value: string;
  setValue: (value: string) => void;
  optionsList: DropdownOption[];
  handleChange?: (value: string) => void;
  boxProps?: any;
  disabled?: boolean;
  showAllOption?: boolean;
  allLabel?: string;
};

const SimpleDropdownFilter = ({
  value,
  setValue,
  optionsList,
  handleChange,
  boxProps,
  disabled = false,
  showAllOption = false,
  allLabel = 'All',
}: SimpleDropdownFilterProps) => {
  const { sx, ...props } = boxProps || {};

  const theme = useTheme();
  const colors = theme.palette;

  const dropdownSx = {
    backgroundColor: colors.DropdownV2.background,
    borderRadius: '4px',
    minWidth: '220px !important',
    width: '220px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',

    '& fieldset': {
      display: 'none',
    },

    '& .MuiSelect-select': {
      ...theme.typography.dropdownText,
      padding: '10px 40px 10px 14px',
      color: colors.DropdownV2.menuTextColor,
    },

    '& .MuiSelect-icon': {
      top: '50% !important',
      transform: 'translateY(-50%)',
      height: '16px',
      width: '16px',
      right: '12px',
      position: 'absolute',
    },
  };

  const handleDropdownChange = (e: any) => {
    const selectedValue = e.target.value;
    setValue(selectedValue);
    handleChange?.(selectedValue);
  };

  const formattedOptions = [
    ...(showAllOption ? [{ label: allLabel, value: 'all' }] : []),
    ...optionsList.map((option) =>
      typeof option === 'string'
        ? { label: option, value: option }
        : option
    ),
  ];

  return (
    <Box sx={{ ...dropdownSx, ...sx }} {...props}>
      <FormControl fullWidth>
        <Select
          value={value}
          onChange={handleDropdownChange}
          disabled={disabled}
          IconComponent={Vector}
          MenuProps={{
            slotProps: {
              paper: {
                sx: {
                  mt: 1,
                  overflow: 'hidden',

                  '& .MuiMenuItem-root': {
                    ...theme.typography.dropdownText,
                    color: colors.DropdownV2.menuItemText,
                    padding: '12px 40px 12px 16px',
                    position: 'relative',
                  },

                  '& .MuiMenuItem-root:hover': {
                    backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
                  },

                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                    color: colors.DropdownV2.menuItemSelectedText,
                  },

                  '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                  },

                  '& .MuiMenuItem-root.Mui-selected::after': {
                    content: '"✓"',
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: colors.DropdownV2.tickColor,
                  },
                  '& .MuiList-root': {
                    backgroundColor: `${colors.DropdownV2.background} !important`,
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  },
                },
              },
            },
          }}
        >
          {formattedOptions.map(({ label, value }) => (
            <MenuItem key={value} value={value} sx={{ minHeight: '46px !important' }}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SimpleDropdownFilter;
